import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthLayout } from '@/components/AuthLayout'

const API_BASE = import.meta.env.VITE_API_URL || ''

export function ForgotPassword() {
  const { t } = useTranslation(['auth', 'common'])
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || t('errors.requestFailed'))
      }

      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.requestFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
        <Card>
          <CardHeader>
            <CardTitle>{t('forgotPassword.title')}</CardTitle>
            <CardDescription>
              {submitted
                ? t('forgotPassword.submittedSubtitle')
                : t('forgotPassword.subtitle')}
            </CardDescription>
          </CardHeader>

          {submitted ? (
            <CardContent>
              <p className="text-sm text-muted-foreground">
                <Trans
                  i18nKey="forgotPassword.submittedBody"
                  ns="auth"
                  values={{ email }}
                  components={[<strong key="email" />]}
                />
              </p>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">{t('fields.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('fields.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </CardContent>
              <CardFooter className="flex-col gap-4">
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
                </Button>
              </CardFooter>
            </form>
          )}

          <CardFooter>
            <p className="text-sm text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline font-medium">
                {t('forgotPassword.backToSignIn')}
              </Link>
            </p>
          </CardFooter>
        </Card>
    </AuthLayout>
  )
}
